import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { sgSupabase } from "../../lib/sgSupabaseClient";
import { fetchOrigins, imeiKey, type SgOrigins } from "./origins";
import { fetchLastPositions } from "./socket";
import { normalize } from "./useSgDeviceConnections";
import { buildUnits, type RawAccount, type RawDevice } from "./cuadre";

// Todo SG de una vez (~27k equipos y ~2.7k cuentas al 27-oct-2026): el cuadre
// cruza cada IMEI con las tres plataformas y su último reporte, y los filtros
// cuentan sobre el total. PostgREST corta en 1000 filas, así que son ~30
// páginas chicas; el export del barrido ya lee esto mismo y más.
const BATCH = 1000;

async function fetchAll<T>(table: string, select: string, onPage?: (n: number) => void): Promise<T[]> {
  const out: T[] = [];
  for (let from = 0; ; from += BATCH) {
    const { data, error } = await sgSupabase
      .from(table)
      .select(select)
      .order("id", { ascending: true })
      .range(from, from + BATCH - 1);
    if (error) throw new Error(error.message);
    out.push(...((data ?? []) as T[]));
    onPage?.(out.length);
    if (!data || data.length < BATCH) return out;
  }
}

const isActiveStatus = (s: any) => ["activo", "active"].includes(String(s?.name_es || s?.name || "").trim().toLowerCase());
const isBillableStatus = (s: any) => String(s?.name || "").trim().toLowerCase() === "facturable";
const toMs = (v: unknown) => {
  if (v == null || v === "") return null;
  const t = new Date(v as string).getTime();
  return Number.isNaN(t) ? null : t;
};

// billings es 1 por cuenta, pero PostgREST lo trae como arreglo (FK user_id).
function accountExtras(u: any): Pick<RawAccount, "company" | "v5CompanyId" | "billingInfo"> {
  const b = Array.isArray(u.billings) ? u.billings[0] : u.billings;
  const str = (v: unknown) => (v == null ? "" : String(v).trim());
  return {
    company: str(b?.business_name) || str(u.company) || null,
    v5CompanyId: str(u.origin_ids?.v5) || null,
    billingInfo: b
      ? { taxId: str(b.tax_id), email: str(b.email), phone: str(b.phone), city: str(b.city), state: str(b.state) }
      : null,
  };
}

export type CuadreStage = "devices" | "platforms" | "reports";

export interface CuadreSource {
  devices: RawDevice[];
  accounts: RawAccount[];
  origins: SgOrigins;
  lastReports: Map<string, number>;
  generatedAt: number;
}

export async function fetchCuadreSource(onProgress?: (stage: CuadreStage, n: number) => void): Promise<CuadreSource> {
  const [rawDevices, rawAccounts] = await Promise.all([
    fetchAll<any>(
      "devices",
      "id, imei, name, plates, phone, simcard, user_id, last_time_connected, activation, created_at, updated_at," +
        " brand, model, brands(brand, model), statuses(name, name_es), device_conditions(name)",
      (n) => onProgress?.("devices", n)
    ),
    fetchAll<any>(
      "users",
      "id, user_name, company, origin, origin_ids, user_statuses(name), account_conditions(name)," +
        " billings(business_name, tax_id, email, phone, city, state)"
    ),
  ]);
  const devices: RawDevice[] = rawDevices.map((d) => ({
    id: d.id,
    imei: imeiKey(d.imei),
    name: d.name ?? null,
    plates: d.plates ?? null,
    userId: d.user_id ?? null,
    active: isActiveStatus(d.statuses),
    condition: d.device_conditions?.name ?? null,
    lastSg: toMs(d.last_time_connected),
    phone: d.phone ?? null,
    simcard: d.simcard ?? null,
    deviceType: [d.brands?.brand ?? d.brand, d.brands?.model ?? d.model].filter(Boolean).join(" ") || null,
    createdAt: toMs(d.activation) ?? toMs(d.created_at),
    updatedAt: toMs(d.updated_at),
  }));
  const accounts: RawAccount[] = rawAccounts.map((u) => ({
    id: u.id,
    name: u.user_name || `#${u.id}`,
    billable: isBillableStatus(u.user_statuses),
    condition: u.account_conditions?.name ?? null,
    origin: Array.isArray(u.origin) ? u.origin : [],
    ...accountExtras(u),
  }));
  const imeis = [...new Set(devices.map((d) => d.imei).filter(Boolean))];
  onProgress?.("platforms", imeis.length);
  const [origins, positions] = await Promise.all([
    fetchOrigins(imeis),
    fetchLastPositions(imeis, { requestId: "cuadre" }).then((m) => {
      onProgress?.("reports", m.size);
      return m;
    }),
  ]);
  const lastReports = new Map<string, number>();
  for (const [imei, raw] of positions) {
    const p = normalize({ ...raw, imei });
    if (p) lastReports.set(imeiKey(imei), p.timestamp);
  }
  return { devices, accounts, origins, lastReports, generatedAt: Date.now() };
}

export function useCuadreSource(onProgress?: (stage: CuadreStage, n: number) => void) {
  return useQuery({
    queryKey: ["sg-cuadre-source"],
    queryFn: () => fetchCuadreSource(onProgress),
    staleTime: 5 * 60_000,
  });
}

// Cuadre, Plataformas e Informes comparten la misma carga (misma queryKey):
// entrar a otra pantalla no vuelve a leer los ~27k equipos.
export function useCuadreUnits() {
  const [progress, setProgress] = useState<{ stage: CuadreStage; n: number }>({ stage: "devices", n: 0 });
  const source = useCuadreSource((stage, n) => setProgress({ stage, n }));
  const units = useMemo(
    () =>
      source.data
        ? buildUnits(source.data.devices, source.data.accounts, source.data.origins, source.data.lastReports, source.data.generatedAt).sort(
            (a, b) => a.accountName.localeCompare(b.accountName, "es") || a.name.localeCompare(b.name, "es")
          )
        : [],
    [source.data]
  );
  return { source, units, progress, resetProgress: () => setProgress({ stage: "devices", n: 0 }) };
}
